import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  newBlock, newEnding, endingsOf, findBlock, updateBlock, removeBlock, moveBlock, addChild, duplicateBlock, publicUrl,
} from "@/lib/forms";
import BlockList from "@/components/forms/BlockList";
import BlockConfig from "@/components/forms/BlockConfig";
import EndingConfig from "@/components/forms/EndingConfig";
import DesignPanel from "@/components/forms/DesignPanel";
import FormRunner from "@/components/forms/FormRunner";
import ErrorBoundary from "@/components/ErrorBoundary";
import FormSettingsPanel from "@/components/forms/FormSettingsPanel";
import ResponsesTable from "@/components/forms/ResponsesTable";
import LogicMap from "@/components/forms/LogicMap";
import { ArrowLeft, ExternalLink, Copy, Trash2, Undo2, Redo2 } from "lucide-react";
import { useConfirm } from "@/components/ConfirmDialog";
import { Rows } from "@/components/ui-helpers";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const SEEDED = ["khush@thickandthin.media", "vedant@thickandthin.media", "prakhar@thickandthin.media"];

export function useMailboxes() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/google/accounts").then((r) => {
      const conn = new Set(r.data.rows.map((x) => x.email));
      const all = [...new Set([...SEEDED, ...conn])];
      setRows(all.map((email) => ({ email, connected: conn.has(email) })));
    });
  }, []);
  return rows;
}

function Preview({ form, sel, always }) {
  const [k, setK] = useState(0);
  return (
    <div className={`${always ? "block" : "hidden 2xl:block"} sticky top-4 h-fit`} data-testid="form-preview">
      <div className="flex items-center justify-between mb-2 text-[11px] text-neutral-500">
        <span>Live preview · starts at the selected block</span>
        <button className="btn-ghost px-2 py-0.5" onClick={() => setK((x) => x + 1)} data-testid="preview-restart">Restart</button>
      </div>
      <div className="relative h-[620px] rounded-md overflow-hidden border border-[var(--border-strong)]">
        <ErrorBoundary message="Preview failed to render.">
          <FormRunner key={`${sel}-${k}`} form={form} preview embedded startAt={sel} />
        </ErrorBoundary>
      </div>
    </div>
  );
}

export default function FormEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [sel, setSel] = useState(null);
  const [tab, setTab] = useState("build");
  const [buildView, setBuildView] = useState("blocks");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [crmFields, setCrmFields] = useState([]);
  const [draft, setDraft] = useState(null);
  const past = useRef([]);
  const future = useRef([]);
  const lastPush = useRef(0);
  const [, force] = useState(0);
  const { confirm } = useConfirm();
  const mailboxes = useMailboxes();
  const draftKey = `form_draft_${id}`;

  useEffect(() => {
    api.get(`/forms/${id}`).then((r) => {
      setForm(r.data); setSel(r.data.blocks?.[0]?.id || null);
      const saved = JSON.parse(localStorage.getItem(draftKey) || "null");
      if (saved && saved.at > (r.data.updated_at || "")) setDraft(saved); else localStorage.removeItem(draftKey);
    });
    api.get("/crm/config").then((r) => setCrmFields(r.data.fields || []));
  }, [id, draftKey]);

  useEffect(() => {
    if (!dirty || !form) return undefined;
    const t = setTimeout(() => localStorage.setItem(draftKey, JSON.stringify({ at: new Date().toISOString(), form })), 800);
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => { clearTimeout(t); window.removeEventListener("beforeunload", warn); };
  }, [dirty, form, draftKey]);

  const edit = useCallback((f) => {
    setForm((cur) => {
      if (cur && Date.now() - lastPush.current > 700) { past.current = [...past.current.slice(-49), cur]; lastPush.current = Date.now(); }
      future.current = [];
      return f;
    });
    setDirty(true); force((x) => x + 1);
  }, []);
  const undo = useCallback(() => {
    if (!past.current.length) return;
    setForm((cur) => { future.current = [cur, ...future.current]; const p = past.current[past.current.length - 1]; past.current = past.current.slice(0, -1); return p; });
    setDirty(true); lastPush.current = 0; force((x) => x + 1);
  }, []);
  const redo = useCallback(() => {
    if (!future.current.length) return;
    setForm((cur) => { past.current = [...past.current, cur]; const n = future.current[0]; future.current = future.current.slice(1); return n; });
    setDirty(true); force((x) => x + 1);
  }, []);
  const setBlocks = (blocks) => edit({ ...form, blocks });

  const save = async (patch = {}) => {
    setSaving(true);
    try {
      const body = { ...form, ...patch };
      const { data } = await api.put(`/forms/${id}`, {
        title: body.title, slug: body.slug, blocks: body.blocks, settings: body.settings, status: body.status,
      });
      setForm(data); setDirty(false); localStorage.removeItem(draftKey);
      toast.success(patch.status ? `Form ${patch.status}` : "Saved");
    } catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const onAction = (kind, bid) => {
    const b = form.blocks;
    if (kind === "up") setBlocks(moveBlock(b, bid, -1));
    if (kind === "down") setBlocks(moveBlock(b, bid, 1));
    if (kind === "dup") setBlocks(duplicateBlock(b, bid));
    if (kind === "del") { setBlocks(removeBlock(b, bid)); if (sel === bid) setSel(null); }
  };
  const add = (type) => { const nb = newBlock(type); setBlocks([...form.blocks, nb]); setSel(nb.id); };
  const endings = form ? endingsOf(form) : [];
  const setEndings = (list) => edit({ ...form, settings: { ...form.settings, endings: list } });
  const addEnding = () => {
    const e = newEnding();
    if (form.blocks.some((x) => ["calendlo", "calendar_meet"].includes(x.type))) {
      Object.assign(e, { title: "Your call is scheduled for {{meeting_datetime}}", body: "We've sent the Google Meet invite to your email.", add_to_calendar: true });
    }
    setEndings([...endings, e]); setSel(e.id);
  };
  const selEnding = endings.find((e) => e.id === sel);
  const addToGroup = (gid, type) => { const nb = newBlock(type); setBlocks(addChild(form.blocks, gid, nb)); setSel(nb.id); };
  const archive = async () => {
    if (!(await confirm({ title: "Archive this form?", body: "The public link will stop working.", danger: true, okLabel: "Archive" }))) return;
    await api.delete(`/forms/${id}`); localStorage.removeItem(draftKey); nav("/forms");
  };
  const back = async () => {
    if (dirty && !(await confirm({ title: "Leave without saving?", body: "Your unsaved edits stay as a draft on this device.", okLabel: "Leave" }))) return;
    nav("/forms");
  };

  useEffect(() => {
    const onKey = (e) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      const k = e.key.toLowerCase();
      const typing = ["INPUT", "TEXTAREA"].includes(e.target.tagName);
      if (k === "s") { e.preventDefault(); document.querySelector("[data-testid=save-form-btn]")?.click(); }
      if (k === "z" && !typing) { e.preventDefault(); e.shiftKey ? redo() : undo(); }
      if (k === "y" && !typing) { e.preventDefault(); redo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo]);

  if (!form) return <div className="p-6"><Rows n={8} /></div>;
  const published = form.status === "published";

  return (
    <div data-testid="form-editor" className="flex flex-col min-h-screen">
      {draft && (
        <div className="flex flex-wrap items-center gap-2 px-4 md:px-6 py-2 bg-[color-mix(in_srgb,var(--warn)_10%,transparent)] border-b border-[color-mix(in_srgb,var(--warn)_40%,transparent)] text-xs" data-testid="draft-banner">
          <span>You have unsaved changes from {new Date(draft.at).toLocaleString("en-IN")}.</span>
          <button className="btn text-xs" onClick={() => { edit(draft.form); setDraft(null); toast.success("Draft restored — Save to publish it"); }} data-testid="draft-restore">Restore</button>
          <button className="btn-ghost btn text-xs" onClick={() => { localStorage.removeItem(draftKey); setDraft(null); }} data-testid="draft-discard">Discard</button>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 px-4 md:px-6 py-3 border-b">
        <button className="btn-ghost btn p-1.5" onClick={back} data-testid="back-to-forms"><ArrowLeft size={14} /></button>
        <input className="bg-transparent text-base font-medium px-2 py-1 rounded border min-w-0 flex-1 md:flex-none md:w-72" value={form.title}
          onChange={(e) => edit({ ...form, title: e.target.value })} data-testid="form-title-input" />
        <div className="flex items-center gap-1 text-xs text-neutral-500 font-mono">
          /f/<input className="px-1.5 py-1 rounded border text-xs w-40" value={form.slug} onChange={(e) => edit({ ...form, slug: e.target.value })} data-testid="form-slug-input" />
        </div>
        <span className={`pill ${published ? "pill-ok" : ""}`} data-testid="form-status">{form.status}</span>
        <div className="flex items-center gap-1 ml-auto">
          <button className="btn-ghost btn p-1.5" title="Undo (⌘Z)" onClick={undo} disabled={!past.current.length} data-testid="undo-btn"><Undo2 size={13} /></button>
          <button className="btn-ghost btn p-1.5" title="Redo (⌘⇧Z)" onClick={redo} disabled={!future.current.length} data-testid="redo-btn"><Redo2 size={13} /></button>
          <button className="btn-ghost btn p-1.5" title="Copy link" onClick={() => { navigator.clipboard?.writeText(publicUrl(form.slug)); toast.success("Link copied"); }} data-testid="copy-link-btn"><Copy size={13} /></button>
          <a className="btn-ghost btn p-1.5" href={publicUrl(form.slug)} target="_blank" rel="noreferrer" title="Open public form" data-testid="open-public-btn"><ExternalLink size={13} /></a>
          {user?.role === "admin" && <button className="btn-ghost btn p-1.5 hover:text-[var(--breach)]" onClick={archive} title="Archive" data-testid="archive-form-btn"><Trash2 size={13} /></button>}
          <button className="btn" onClick={() => save({ status: published ? "draft" : "published" })} disabled={saving || user?.role !== "admin"} data-testid="publish-toggle-btn">{published ? "Unpublish" : "Publish"}</button>
          <button className="btn btn-primary" onClick={() => save()} disabled={saving || !dirty || user?.role !== "admin"} title={user?.role !== "admin" ? "View only: admins edit forms" : undefined} data-testid="save-form-btn">{saving ? "Saving…" : dirty ? "Save" : "Saved"}</button>
        </div>
      </div>
      <div className="flex gap-1 px-4 md:px-6 border-b text-[13px]">
        {["build", "design", "settings", "responses"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
            className={`px-3 py-2 capitalize border-b-2 -mb-px transition-colors ${tab === t ? "border-white text-white" : "border-transparent text-neutral-500 hover:text-neutral-300"}`}>{t}</button>
        ))}
      </div>
      <div className="flex-1 p-4 md:p-6">
        {tab === "build" && (
          <div className="flex gap-1 mb-3" data-testid="build-view-toggle">
            {[["blocks", "Blocks"], ["map", "Logic map"]].map(([k, l]) => (
              <button key={k} onClick={() => setBuildView(k)} className={`btn text-xs ${buildView === k ? "btn-primary" : ""}`} data-testid={`build-view-${k}`}>{l}</button>
            ))}
          </div>
        )}
        {tab === "build" && buildView === "map" && (
          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_420px] gap-4">
            <LogicMap blocks={form.blocks} endings={endings} variables={form.settings?.variables || []} sel={sel} onSelect={setSel} />
            <div className="panel p-4 h-fit">
              {selEnding ? (
                <EndingConfig ending={selEnding} canDelete={endings.length > 1}
                  onChange={(eid, p) => setEndings(endings.map((e) => (e.id === eid ? { ...e, ...p } : e)))}
                  onDelete={(eid) => { setEndings(endings.filter((e) => e.id !== eid)); setSel(null); }} />
              ) : (
                <BlockConfig block={findBlock(form.blocks, sel)} crmFields={crmFields} mailboxes={mailboxes} blocks={form.blocks} endings={endings} variables={form.settings?.variables || []}
                  topLevel={form.blocks.some((b) => b.id === sel)} onChange={(bid, patch) => setBlocks(updateBlock(form.blocks, bid, patch))} />
              )}
            </div>
          </div>
        )}
        {tab === "build" && buildView === "blocks" && (
          <div className="grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)] 2xl:grid-cols-[300px_420px_minmax(0,1fr)] gap-4">
            <div className="panel p-2 h-fit"><BlockList blocks={form.blocks} selectedId={sel} onSelect={setSel} onAction={onAction} onAdd={add} onAddChild={addToGroup} endings={endings} onAddEnding={addEnding} /></div>
            <div className="panel p-4 h-fit">
              {selEnding ? (
                <EndingConfig ending={selEnding} canDelete={endings.length > 1}
                  onChange={(eid, p) => setEndings(endings.map((e) => (e.id === eid ? { ...e, ...p } : e)))}
                  onDelete={(eid) => { setEndings(endings.filter((e) => e.id !== eid)); setSel(null); }} />
              ) : (
                <BlockConfig block={findBlock(form.blocks, sel)} crmFields={crmFields} mailboxes={mailboxes} blocks={form.blocks} endings={endings} variables={form.settings?.variables || []}
                  topLevel={form.blocks.some((b) => b.id === sel)} onChange={(bid, patch) => setBlocks(updateBlock(form.blocks, bid, patch))} />
              )}
            </div>
            <Preview form={form} sel={sel} />
          </div>
        )}
        {tab === "design" && (
          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,560px)_minmax(0,1fr)] gap-4">
            <DesignPanel form={form} setForm={edit} />
            <Preview form={form} sel={sel} always />
          </div>
        )}
        {tab === "settings" && <FormSettingsPanel form={form} setForm={edit} />}
        {tab === "responses" && <ResponsesTable form={form} />}
      </div>
    </div>
  );
}
